import AddIcon from '@mui/icons-material/Add';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import type { User } from '@supabase/supabase-js';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AddImmobilieModal } from '@/components/AddImmobilieModal';
import { BetaTermsModal } from '@/components/BetaTermsModal';
import { FeedbackDialog } from '@/components/FeedbackDialog';
// Lazy: der Bug-Dialog (inkl. Annotations-Logik) wird erst geladen, wenn der
// Nutzer den „Bug melden"-Button drückt — nicht im AppShell-Bundle für alle.
const BugReportDialog = lazy(() =>
  import('@/components/BugReportDialog').then((m) => ({ default: m.BugReportDialog }))
);
import { captureViewport, type CapturedScreenshot } from '@/lib/bugReport';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import { LegalConsentModal } from '@/components/LegalConsentModal';
import { LegalFooter } from '@/components/LegalFooter';
import { useAffiliateAttribution } from '@/hooks/useAffiliateAttribution';
import { useBetaTermsConsent } from '@/hooks/useBetaTermsConsent';
import { useLegalConsent } from '@/hooks/useLegalConsent';
import { AnalyticsEvent, flushAnalyticsNow, trackAnalytics } from '@/lib/analytics';
import {
  isLocalDemoSession,
  LOCAL_MOCK_AUTH_CHANGED_EVENT,
} from '@/lib/localDevMockAuth';
import { isPreviewMode } from '@/lib/previewMode';
import { PreviewBanner, PREVIEW_BANNER_HEIGHT } from '@/components/PreviewBanner';
import { isSuperuserEmail } from '@/lib/superuser';
import { useDeveloperMode } from '@/hooks/useDeveloperMode';
import { supabase } from '@/lib/supabase';
import type { AppShellOutletContext } from '@/layout/appShellOutletContext';
import { brandColors } from '@/theme/brandColors';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@/components/LanguageSelector';

const DRAWER_WIDTH = 240;
const SIDEBAR_BG = brandColors.sidebar;

function userInitials(user: User | null): string {
  if (!user) return '?';
  const meta = user.user_metadata as { full_name?: string; name?: string } | undefined;
  const name = (meta?.full_name || meta?.name || '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  const em = user.email?.trim() || '';
  if (em) return em.slice(0, 2).toUpperCase();
  return '?';
}

function displayName(user: User | null): string {
  if (!user) return 'Gast';
  const meta = user.user_metadata as { full_name?: string; name?: string } | undefined;
  return (meta?.full_name || meta?.name || user.email || 'Konto').trim();
}

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);
  const [bugShot, setBugShot] = useState<CapturedScreenshot | null>(null);
  const [capturing, setCapturing] = useState(false);
  // Wird beim ersten Klick true → Lazy-Dialog wird geladen und bleibt gemountet
  // (damit Öffnen-/Schließen-Animation erhalten bleibt).
  const [bugDialogLoaded, setBugDialogLoaded] = useState(false);

  const startBugReport = useCallback(async () => {
    setBugDialogLoaded(true);
    setCapturing(true);
    try {
      const shot = await captureViewport();
      setBugShot(shot);
      setBugOpen(true);
    } catch (e) {
      console.error('[bug-report] capture failed', e);
      // Trotzdem den Dialog ohne Screenshot öffnen — Kommentar geht raus.
      setBugShot(null);
      setBugOpen(true);
    } finally {
      setCapturing(false);
    }
  }, []);
  const [user, setUser] = useState<User | null>(null);
  const [profileSuperuser, setProfileSuperuser] = useState(false);
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const refreshSessionUser = useCallback(() => {
    if (!supabase) return;
    void supabase.auth.getSession().then(({ data }) => setUser(data.session?.user ?? null));
  }, []);

  useEffect(() => {
    if (!supabase) return;
    refreshSessionUser();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null);
    });
    window.addEventListener(LOCAL_MOCK_AUTH_CHANGED_EVENT, refreshSessionUser);
    return () => {
      sub.subscription.unsubscribe();
      window.removeEventListener(LOCAL_MOCK_AUTH_CHANGED_EVENT, refreshSessionUser);
    };
  }, [refreshSessionUser]);

  useEffect(() => {
    if (!supabase || !user?.id) {
      setProfileSuperuser(false);
      return;
    }
    let cancelled = false;
    void supabase
      .from('profiles')
      .select('is_superuser')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setProfileSuperuser(Boolean(data?.is_superuser));
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const superuser = profileSuperuser || isSuperuserEmail(user?.email);
  // Developer Mode AUS → Superuser sieht das Produkt wie ein normaler Kunde
  // (Admin-Nav etc. ausgeblendet). Echter Superuser-Status bleibt unberührt;
  // nur die Darstellung folgt `effectiveSuperuser`.
  const developerMode = useDeveloperMode();
  const effectiveSuperuser = superuser && developerMode;

  useEffect(() => {
    if (!user) return;
    const t = window.setTimeout(() => {
      trackAnalytics(AnalyticsEvent.page_view, { path: pathname });
    }, 380);
    return () => window.clearTimeout(t);
  }, [pathname, user]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') flushAnalyticsNow();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const signOut = useCallback(async () => {
    flushAnalyticsNow();
    if (!supabase) return;
    await supabase.auth.signOut();
    navigate('/');
  }, [navigate]);

  const { status: consentStatus, acceptAll } = useLegalConsent(user);
  const { status: betaTermsStatus, accept: acceptBetaTerms } = useBetaTermsConsent(user);
  // Ordnet den User nach Login einem Affiliate-Partner zu, falls ein
  // ?ref=-Code im Storage liegt (first-touch, serverseitig idempotent).
  useAffiliateAttribution(user);

  const outletContext = useMemo<AppShellOutletContext>(
    () => ({ openAddImmobilie: () => setAddOpen(true) }),
    []
  );

  const listSelected = pathname === '/' || pathname === '';
  const vergleichSelected = pathname.startsWith('/vergleich');
  const renditerechnerSelected = pathname.startsWith('/renditerechner');
  const paketeSelected = pathname.startsWith('/pakete');
  const adminAnalyticsSelected = pathname.startsWith('/admin/analytics');
  const dokuSelected = pathname.startsWith('/doku');
  const authHref = `/auth?returnTo=${encodeURIComponent(`${pathname}${search}`)}`;

  const navButtonSx = {
    borderRadius: 1.5,
    minHeight: 36,
    px: 1.5,
    py: 0.75,
    color: 'rgba(255,255,255,0.55)',
    transition: 'background-color 0.1s ease, color 0.1s ease',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' },
    '&.Mui-selected': {
      bgcolor: 'rgba(255,255,255,0.15)',
      color: '#fff',
    },
  };

  const drawerInner = (
    <>
      <Box
        sx={{
          height: 64,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <ExploreOutlinedIcon sx={{ color: '#fff', fontSize: 20 }} />
        <Typography sx={{ fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif', fontWeight: 700, color: '#fff', fontSize: '0.95rem', letterSpacing: '-0.02em' }}>
          ImmoKompass
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <LanguageSelector color="rgba(255,255,255,0.85)" />
        </Box>
      </Box>

      <Box sx={{ px: 1.5, pt: 2, flexShrink: 0 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setAddOpen(true);
            closeMobile();
          }}
          sx={{
            minHeight: 38,
            borderRadius: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
          }}
        >
          {t('common.newProperty')}
        </Button>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', mt: 3 }}>
        <Typography
          variant="caption"
          sx={{
            px: 2,
            mb: 0.5,
            display: 'block',
            fontSize: '0.6875rem',
            letterSpacing: '0.08em',
            color: 'rgba(251,247,242,0.55)',
          }}
        >
          {t('nav.section')}
        </Typography>
        <List dense disablePadding sx={{ px: 1 }}>
          <ListItemButton
            component={Link}
            to="/"
            selected={listSelected}
            onClick={closeMobile}
            sx={navButtonSx}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
              <HomeOutlinedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary={t('nav.properties')}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: '0.875rem' }}
            />
          </ListItemButton>
          <ListItemButton
            component={Link}
            to="/vergleich"
            selected={vergleichSelected}
            onClick={closeMobile}
            sx={navButtonSx}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
              <CompareArrowsIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary={t('nav.compare')}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: '0.875rem' }}
            />
          </ListItemButton>
          <ListItemButton
            component={Link}
            to="/renditerechner"
            selected={renditerechnerSelected}
            onClick={closeMobile}
            sx={navButtonSx}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
              <CalculateOutlinedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary={t('nav.calculator')}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: '0.875rem' }}
            />
          </ListItemButton>
          <ListItemButton
            component={Link}
            to="/pakete"
            selected={paketeSelected}
            onClick={closeMobile}
            sx={navButtonSx}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
              <CreditCardOutlinedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary={t('nav.packages')}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: '0.875rem' }}
            />
          </ListItemButton>
          {effectiveSuperuser ? (
            <ListItemButton
              component={Link}
              to="/admin/analytics"
              selected={adminAnalyticsSelected}
              onClick={closeMobile}
              sx={navButtonSx}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                <AssessmentOutlinedIcon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary={t('nav.admin')}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: '0.875rem' }}
              />
            </ListItemButton>
          ) : null}
          {effectiveSuperuser ? (
            <ListItemButton
              component={Link}
              to="/doku"
              selected={dokuSelected}
              onClick={closeMobile}
              sx={navButtonSx}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                <MenuBookOutlinedIcon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary={t('nav.docs')}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: '0.875rem' }}
              />
            </ListItemButton>
          ) : null}
        </List>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          px: 1.5,
          py: 2,
        }}
      >
        {/* Feedback + Bug melden — unten, in der Nav-Optik. */}
        <List dense disablePadding sx={{ mb: 1.5 }}>
          <ListItemButton
            onClick={() => {
              if (!user) setAuthPromptOpen(true);
              else setFeedbackOpen(true);
              closeMobile();
            }}
            sx={navButtonSx}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
              <FeedbackOutlinedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary={t('nav.feedback')}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: '0.875rem' }}
            />
          </ListItemButton>
          {user ? (
            <ListItemButton
              onClick={() => {
                closeMobile();
                void startBugReport();
              }}
              disabled={capturing}
              sx={navButtonSx}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                {capturing ? (
                  <CircularProgress size={16} sx={{ color: 'inherit' }} />
                ) : (
                  <BugReportOutlinedIcon sx={{ fontSize: 18 }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={capturing ? 'Screenshot…' : t('nav.reportBug')}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: '0.875rem' }}
              />
            </ListItemButton>
          ) : null}
        </List>
        <ListItemButton
          {...(user ? { component: Link, to: '/einstellungen' } : {})}
          onClick={() => {
            if (!user) {
              setAuthPromptOpen(true);
            }
            closeMobile();
          }}
          sx={{
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            mb: 1,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            '&:hover .profile-chevron': { opacity: 1 },
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: '0.8125rem',
              fontWeight: 700,
              bgcolor: brandColors.primary,
            }}
          >
            {userInitials(user)}
          </Avatar>
          <ListItemText
            sx={{ ml: 1.5, minWidth: 0 }}
            primary={displayName(user)}
            secondary={
              user
                ? isLocalDemoSession()
                  ? `${user.email ?? ''} · Lokal angemeldet (Testdaten)`
                  : (user.email ?? '')
                : t('nav.notSignedIn')
            }
            primaryTypographyProps={{ noWrap: true, fontWeight: 600, fontSize: '0.8125rem', color: '#fff' }}
            secondaryTypographyProps={{
              noWrap: true,
              fontSize: '0.6875rem',
              color: 'rgba(255,255,255,0.45)',
            }}
          />
          <ChevronRightIcon
            className="profile-chevron"
            sx={{
              color: 'rgba(255,255,255,0.35)',
              fontSize: 20,
              opacity: 0,
              transition: 'opacity 0.15s ease',
            }}
          />
        </ListItemButton>
        {user ? (
          <Button
            fullWidth
            startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
            onClick={() => void signOut()}
            sx={{
              justifyContent: 'flex-start',
              color: 'rgba(255,255,255,0.45)',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8125rem',
              px: 1.5,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' },
            }}
          >
            {t('nav.logout')}
          </Button>
        ) : (
          <Button
            fullWidth
            component={Link}
            to={authHref}
            startIcon={<LoginOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={closeMobile}
            sx={{
              justifyContent: 'flex-start',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              bgcolor: 'rgba(255,255,255,0.12)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            }}
          >
            {t('nav.loginRegister')}
          </Button>
        )}
      </Box>
    </>
  );

  const drawerPaperSx = {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box' as const,
    borderRight: 'none',
    bgcolor: SIDEBAR_BG,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  };

  const previewActive = isPreviewMode();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', overflowX: 'hidden', pt: previewActive ? `${PREVIEW_BANNER_HEIGHT}px` : 0 }}>
      <PreviewBanner fixed />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { xs: 'flex', md: 'none' },
          bgcolor: SIDEBAR_BG,
          color: '#fff',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          // Wenn Preview-Banner sichtbar: AppBar nach unten verschieben.
          top: previewActive ? `${PREVIEW_BANNER_HEIGHT}px` : 0,
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 48 }}>
          <IconButton
            color="inherit"
            edge="start"
            aria-label={t('nav.openMenu')}
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 1, minWidth: 48, minHeight: 48 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={700} component="div" sx={{ flexGrow: 1, fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif' }} noWrap>
            ImmoKompass
          </Typography>
          <LanguageSelector color="#fff" />
          {superuser && !developerMode ? (
            <Chip
              label="Kundensicht"
              size="small"
              color="warning"
              onClick={() => navigate('/einstellungen')}
              title="Developer Mode ist aus — du siehst die App wie ein normaler Kunde. Klicken, um in den Einstellungen zurückzuschalten."
              sx={{ fontWeight: 700, cursor: 'pointer' }}
            />
          ) : null}
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }} aria-label="Navigation">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={closeMobile}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': drawerPaperSx,
          }}
        >
          {drawerInner}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': drawerPaperSx,
          }}
          open
        >
          {drawerInner}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          maxWidth: { xs: '100vw', md: 'none' },
          display: 'flex',
          flexDirection: 'column',
          bgcolor: brandColors.cream,
          pt: { xs: '48px', md: 0 },
          pb: { xs: 'max(16px, env(safe-area-inset-bottom))', md: 0 },
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Outlet context={outletContext} />
          <LegalFooter />
        </Box>
      </Box>

      <AddImmobilieModal open={addOpen} onClose={() => setAddOpen(false)} navigate={navigate} />
      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        contextKind="global"
        onAuthRequired={() => setAuthPromptOpen(true)}
      />
      <LegalConsentModal
        open={consentStatus === 'required' && !pathname.startsWith('/agb') && !pathname.startsWith('/datenschutz') && !pathname.startsWith('/impressum') && !pathname.startsWith('/terms') && !pathname.startsWith('/privacy')}
        onAccept={acceptAll}
      />
      {/* Beta-Disclaimer-Modal — erscheint erst NACH dem Legal-Consent-Modal,
          damit User die Reihenfolge Rechtsdokumente → Beta-Hinweis durchklickt
          und nicht beide gleichzeitig sieht. Auch hier die Legal-Pages
          (AGB/Datenschutz/Impressum) ausnehmen — sonst sperren wir den User
          aus den Texten aus die er gerade lesen will. */}
      <BetaTermsModal
        open={
          consentStatus !== 'required' &&
          betaTermsStatus === 'required' &&
          !pathname.startsWith('/agb') &&
          !pathname.startsWith('/datenschutz') &&
          !pathname.startsWith('/impressum') &&
          !pathname.startsWith('/terms') &&
          !pathname.startsWith('/privacy')
        }
        onAccept={acceptBetaTerms}
      />
      <Dialog
        open={authPromptOpen}
        onClose={() => setAuthPromptOpen(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="auth-prompt-title"
      >
        <DialogTitle id="auth-prompt-title">Anmeldung erforderlich</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Bitte melde dich an oder registriere dich, um auf deine Kontoeinstellungen zuzugreifen.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAuthPromptOpen(false)} color="inherit">
            Schließen
          </Button>
          <Button
            component={Link}
            to={authHref}
            variant="contained"
            onClick={() => setAuthPromptOpen(false)}
          >
            {t('nav.loginRegister')}
          </Button>
        </DialogActions>
      </Dialog>

      {bugDialogLoaded ? (
        <Suspense fallback={null}>
          <BugReportDialog open={bugOpen} screenshot={bugShot} onClose={() => setBugOpen(false)} />
        </Suspense>
      ) : null}
    </Box>
  );
}
